import type { CurrentSeasonArea } from '@protector/entities/property/models';
import type { LoadWindowEventsRequest } from '@protector/entities/region/models';
import type { Season } from '@protector/entities/season/models';
import { isSelectedSeasonInactive } from '@protector/entities/season/utils';
import * as Utils from 'entities/region/region.utils';
import type { Monitoring, TimelineWindowEvents, TimelineWindowEventsFromBff } from './timeline.models';
import { EventType } from './timeline.models';

export { isSelectedSeasonInactive };

const isBrowser = globalThis.window !== undefined;

export const scheduleSeverityTimeout = (callback: () => void, delay: number) =>
  isBrowser && globalThis.window ? globalThis.window.setTimeout(callback, delay) : globalThis.setTimeout(callback, delay);

export const clearSeverityTimeoutById = (timeoutId?: ReturnType<typeof setTimeout>) => {
  if (isBrowser && globalThis.window) {
    globalThis.window.clearTimeout(timeoutId);
  } else {
    globalThis.clearTimeout(timeoutId);
  }
};

interface ShouldShowCropEndedFieldsParams {
  allSeasons: Season[] | undefined;
  selectedSeasonIds: string[];
  currentAreaSeasons: CurrentSeasonArea[];
  isLoadingCurrentAreaSeasons: boolean;
}

const hasActiveAreaInSelectedSeason = (currentAreaSeasons: CurrentSeasonArea[], selectedSeasonId: string): boolean => {
  const now = new Date();
  return currentAreaSeasons.some(area => area.seasonId === selectedSeasonId && new Date(area.endsAt) > now);
};

export const shouldShowCropEndedFields = ({
  allSeasons,
  selectedSeasonIds,
  currentAreaSeasons,
  isLoadingCurrentAreaSeasons
}: ShouldShowCropEndedFieldsParams): boolean => {
  if (isSelectedSeasonInactive(allSeasons, selectedSeasonIds)) return true;
  if (isLoadingCurrentAreaSeasons || selectedSeasonIds.length !== 1) return false;
  return !hasActiveAreaInSelectedSeason(currentAreaSeasons, selectedSeasonIds[0]);
};

const buildMonitoringWindow = (
  response: TimelineWindowEvents | TimelineWindowEventsFromBff,
  isWindowEventsFromBFF: boolean
): TimelineWindowEvents['monitoring_window'] | TimelineWindowEventsFromBff['monitoring_window'] | undefined => {
  if (!response.monitoring_window) return undefined;

  if (isWindowEventsFromBFF) {
    return {
      ...response.monitoring_window,
      type: EventType.MONITORING
    } as TimelineWindowEventsFromBff['monitoring_window'];
  }

  return {
    ...Utils.sortSamples({
      ...response.monitoring_window,
      type: EventType.MONITORING
    } as Monitoring),
    type: EventType.MONITORING
  } as TimelineWindowEvents['monitoring_window'];
};

export const transformWindowEventsResponse = <T>({
  request,
  response,
  enableMultiplePhotosNotes,
  isWindowEventsFromBFF = false
}: {
  request: LoadWindowEventsRequest;
  response: TimelineWindowEvents | TimelineWindowEventsFromBff;
  enableMultiplePhotosNotes: boolean;
  isWindowEventsFromBFF?: boolean;
}): T => {
  const monitoring_window = buildMonitoringWindow(response, isWindowEventsFromBFF);
  return {
    startDate: request.start,
    endDate: request.end,
    annotation_window: response.annotation_window && {
      ...response.annotation_window,
      annotations: response.annotation_window.annotations.map(annotation =>
        Utils.parsedDataToAnnotations(annotation, enableMultiplePhotosNotes)
      ),
      type: EventType.ANNOTATION
    },
    monitoring_window: monitoring_window,
    phenology_window: response.phenology_window && {
      ...response.phenology_window,
      type: EventType.PHENOLOGY
    },
    harvest_window: response.harvest_window && {
      ...response.harvest_window,
      type: EventType.HARVESTING
    },
    irrigation_window: response.irrigation_window && {
      ...response.irrigation_window,
      type: EventType.IRRIGATION
    },
    spray_window: response.spray_window && {
      ...response.spray_window,
      type: EventType.APPLICATION
    },
    planting_window: response.planting_window && {
      ...response.planting_window,
      type: EventType.PLANTING
    },
    emergence_window: response.emergence_window && {
      ...response.emergence_window,
      type: EventType.EMERGENCE
    },
    flowering_window: response.flowering_window && {
      ...response.flowering_window,
      type: EventType.FLOWERING
    },
    detasseling_window: response.detasseling_window && {
      ...response.detasseling_window,
      type: EventType.DETASSELING
    },
    roguing_window: response.roguing_window
      ? ({
          ...response.roguing_window,
          type: EventType.ROGUING
        } as TimelineWindowEvents['roguing_window'])
      : undefined,
    male_destruction_window: response.male_destruction_window
      ? ({
          ...response.male_destruction_window,
          type: EventType.MALE_DESTRUCTION
        } as TimelineWindowEvents['male_destruction_window'])
      : undefined,
    flaming_window: response.flaming_window
      ? ({
          ...response.flaming_window,
          type: EventType.FLAMING
        } as TimelineWindowEvents['flaming_window'])
      : undefined,
    custom_forms_window: response.custom_forms_window && {
      ...response.custom_forms_window,
      type: EventType.CUSTOM_FORM
    },
    areaId: request.area_id
  } as unknown as T;
};

interface TimelineEventToIgnoreByFlags {
  P40_46597_showing_the_platings_section?: boolean | string;
  P40_56666_enable_flaming_operation_type?: boolean | string;
  P40_57972_enable_emergence_timeline?: boolean | string;
  [key: string]: boolean | string | undefined;
}

export const getIgnoreListEvents = (flags: TimelineEventToIgnoreByFlags | null) => {
  if (!flags) return [EventType.STATIC_POINT];
  const EVENT_FLAG_RULES: { flag: keyof TimelineEventToIgnoreByFlags; events: EventType[] }[] = [
    {
      flag: 'P40_46597_showing_the_platings_section',
      events: [EventType.PLANTING]
    },
    {
      flag: 'P40_56666_enable_flaming_operation_type',
      events: [EventType.FLAMING]
    },
    {
      flag: 'P40_57972_enable_emergence_timeline',
      events: [EventType.EMERGENCE]
    }
  ];

  const conditionalIgnoredEvents = EVENT_FLAG_RULES.filter(rule => !flags?.[rule.flag]).flatMap(rule => rule.events);
  return [EventType.STATIC_POINT, ...conditionalIgnoredEvents];
};
